/**
 * Boombox navigation registry — single source of truth for primary
 * navigation surfaces. Both the left sidebar (`Sidebar.tsx`) and the
 * agent palette (`agent-config.tsx` JUMP TO list) read from this
 * module so a label or icon change happens in one place.
 *
 * Each entry tags which surfaces it should appear on:
 *
 *   sidebar  → renders inside the appropriate AppSidebarRegularGroup
 *   palette  → renders in agent palette JUMP TO
 *
 * The Boombox sidebar groups items under headers (Contacts, Content,
 * Communications). We carry that group attribution here so the
 * sidebar can rebuild the same groupings without duplicating data.
 *
 * The admin pages live in `settings-nav.ts`, which feeds both the
 * sidebar's Settings group and the ⌘K palette's SETTINGS section.
 */

import { Calendar, FileText, LayoutTemplate, type LucideIcon, Mail, Send, Users, Users2 } from 'lucide-react'

import { BOOMBOX_ROUTES, type BoomboxRoute } from './routes'

export type NavSurface = 'sidebar' | 'palette'
export type NavGroup = 'contacts' | 'content' | 'communications'

export interface NavEntry {
  id: string
  label: string
  href: BoomboxRoute
  icon: LucideIcon
  surfaces: NavSurface[]
  group: NavGroup
  keywords?: string
}

export const BOOMBOX_NAV: NavEntry[] = [
  {
    id: 'people',
    label: 'People',
    href: BOOMBOX_ROUTES.PEOPLE,
    icon: Users,
    surfaces: ['sidebar', 'palette'],
    group: 'contacts',
    keywords: 'contacts recipients',
  },
  {
    id: 'lists',
    label: 'Lists',
    href: BOOMBOX_ROUTES.LISTS,
    icon: Users2,
    surfaces: ['sidebar', 'palette'],
    group: 'contacts',
    keywords: 'segment audience',
  },
  {
    id: 'templates',
    label: 'Templates',
    href: BOOMBOX_ROUTES.TEMPLATES,
    icon: FileText,
    surfaces: ['sidebar', 'palette'],
    group: 'content',
  },
  {
    id: 'layouts',
    label: 'Layouts',
    href: BOOMBOX_ROUTES.LAYOUTS,
    icon: LayoutTemplate,
    surfaces: ['sidebar', 'palette'],
    group: 'content',
  },
  {
    id: 'send',
    label: 'Send',
    href: BOOMBOX_ROUTES.SEND,
    icon: Send,
    surfaces: ['sidebar', 'palette'],
    group: 'communications',
    keywords: 'compose new email blast',
  },
  {
    id: 'messages',
    label: 'Messages',
    href: BOOMBOX_ROUTES.MESSAGES,
    icon: Mail,
    surfaces: ['sidebar', 'palette'],
    group: 'communications',
    keywords: 'communications history sent',
  },
  {
    id: 'schedule',
    label: 'Schedule',
    href: BOOMBOX_ROUTES.SCHEDULE,
    icon: Calendar,
    surfaces: ['sidebar', 'palette'],
    group: 'communications',
    keywords: 'scheduled queued',
  },
]

export function navFor(surface: NavSurface): NavEntry[] {
  return BOOMBOX_NAV.filter((e) => e.surfaces.includes(surface))
}

export function navGroup(surface: NavSurface, group: NavGroup): NavEntry[] {
  return BOOMBOX_NAV.filter((e) => e.surfaces.includes(surface) && e.group === group)
}
